import type {
    GenericObject,
    IPenciledInputs,
    IPencilOfferInputs,
    Offer,
    OfferType,
    PartialRestoreRecord,
    PenciledInputOfferType,
    ResidualValueType
} from '@makemydeal/dr-platform-types';
import { BALLOON, CASH, FINANCE, LEASE, OFFER_ENCRYPTION_FIELDS } from '@makemydeal/dr-platform-types';
import type { PartialPenciledInputsWithAllTypes, PenciledInputsState } from '../../../types/commonTypes';
import type { GridPaymentGridData } from '../../../types/paymentTypes';
import { pickGridWrittenOverrides } from '../../../utils/gridWrittenOverrides';
import * as types from '../../../types/actionTypes';
import * as selectors from '../../../selectors';
import { parseTermId } from '../../../utils/termIdParser';
import {
    dropPinnedOverridesForTerm,
    dropProgramDerivedLenderOverrides,
    getTermLenderCode,
    resolveAcqFeeReset,
    getTermMonthsById,
    getTermProgramKey,
    resetPenciledInputsOverrides,
    updateInputWithValue,
    updateOfferTypeWithInputs
} from '../helpers';
import { FeesCategoriesType } from '../../../constants/payment';
import { withoutTypedBuyRate } from '../../../utils/typedBuyRate';
import { pickFallbackSnapshot, wipeOverridesNode } from './overridesFallbackHelpers';

const userPricedGridOverrides = (inputs: IPencilOfferInputs, dealTermMonths: number | undefined): Record<number, GenericObject> => {
    const gridData = inputs.paymentGridData as GridPaymentGridData | undefined;
    return Object.keys(gridData?.userPricedTerms ?? {}).reduce<Record<number, GenericObject>>((kept, key) => {
        const termMonths = Number(key);
        if (termMonths === dealTermMonths) return kept;
        const picked = pickGridWrittenOverrides(inputs.overridesByTerm?.[termMonths], gridData?.gridWrittenTerms?.[termMonths]);
        if (picked) kept[termMonths] = picked;
        return kept;
    }, {});
};

export const handleVehicleAndTradeCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    offer: Offer,
    isUserProgramQuotes: boolean,
    isPaymentGridRedesignEnabled = false
): PartialPenciledInputsWithAllTypes => {
    switch (action.type) {
        case types.APPLY_DEALER_DISCOUNTS: {
            const dealerDiscounts = action.payload;
            const financeOfferPrice = selectors.getDiscountedOfferPrice(state, dealerDiscounts, FINANCE);
            const leaseOfferPrice = selectors.getDiscountedOfferPrice(state, dealerDiscounts, LEASE);
            const cashOfferPrice = selectors.getDiscountedOfferPrice(state, dealerDiscounts, CASH);
            const balloonOfferPrice = selectors.getDiscountedOfferPrice(state, dealerDiscounts, BALLOON);
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [FINANCE]: {
                        ...state.byOfferType?.[FINANCE],
                        discountApplied: true,
                        offerPrice: financeOfferPrice
                    },
                    [LEASE]: {
                        ...state.byOfferType?.[LEASE],
                        discountApplied: true,
                        offerPrice: leaseOfferPrice
                    },
                    [CASH]: {
                        ...state.byOfferType?.[CASH],
                        discountApplied: true,
                        offerPrice: cashOfferPrice
                    },
                    [BALLOON]: {
                        ...state.byOfferType?.[BALLOON],
                        discountApplied: true,
                        offerPrice: balloonOfferPrice
                    }
                },
                dealerDiscounts
            };
        }

        case types.UPDATE_PROFITABILITY_DATA: {
            const { offerType: actionOfferType, profitabilityData } = action.payload;

            // For cash offers
            if (actionOfferType === CASH) {
                return {
                    ...state,
                    byOfferType: {
                        ...state.byOfferType,
                        [CASH]: {
                            ...state.byOfferType?.[CASH],
                            ...profitabilityData
                        }
                    } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
                };
            }

            // For finance/lease
            return state;
        }

        case types.SELECTED_TERM: {
            const leaseTermId = action.payload;
            const offerT = action.meta.middleware.offerType || offerType;
            const prevOfferInputs = state.byOfferType?.[offerT as keyof typeof state.byOfferType];
            let inputs: IPencilOfferInputs = updateInputWithValue(prevOfferInputs, 'term', leaseTermId);

            // DE754192: a payment-grid cell click dispatches SELECTED_TERM, and the selected cell can be priced
            // by a different lender than the current one. The Acq Fee / MRM overrides carried over from the
            // previously applied program would then be forced onto the new lender's payment, so drop them here —
            // the request this action triggers is built after the reducer runs, so it goes out clean.
            if (offerT === LEASE && prevOfferInputs?.paymentGridData?.paymentGridToggle) {
                const prevLenderCode = getTermLenderCode(offer, prevOfferInputs.term);
                const nextLenderCode = getTermLenderCode(offer, leaseTermId);
                const lenderChanged = Boolean(prevLenderCode && nextLenderCode && prevLenderCode !== nextLenderCode);

                // Restate the marker per transition: if only cleared on re-seed it would survive a failed
                // payment and then fire on an unrelated same-lender selection.
                inputs = lenderChanged
                    ? dropProgramDerivedLenderOverrides(inputs)
                    : { ...inputs, pendingLenderProgramSync: undefined };

                // The same reasoning one level finer: under the redesign each column is pinned to its own program, so
                // selecting a cell on a *different program* - which the same lender can offer, and the lender-code test
                // above cannot see - must shed the rate pinned for the term being selected.
                //
                // Gated on recorded column sources, which only the redesign writes and only for Lease, so this is inert
                // with the toggle off where a per-term rate is seeded rather than pinned. Cast because columnSources is
                // not on the published PaymentGridData type yet.
                const isRedesignGrid = (prevOfferInputs.paymentGridData as any)?.columnSources != null;
                const prevProgramKey = getTermProgramKey(offer, prevOfferInputs.term);
                const nextProgramKey = getTermProgramKey(offer, leaseTermId);
                if (isRedesignGrid && prevProgramKey && nextProgramKey && prevProgramKey !== nextProgramKey) {
                    inputs = dropPinnedOverridesForTerm(inputs, getTermMonthsById(offer, leaseTermId));
                }
            }

            if (offerT === LEASE && !isUserProgramQuotes && offer?.featureToggles?.enableExplicitLenderP1) {
                const prevMonths =
                    getTermMonthsById(offer, prevOfferInputs?.term) ?? parseTermId(prevOfferInputs?.term)?.termIdParams?.term;
                const newMonths = getTermMonthsById(offer, leaseTermId) ?? parseTermId(leaseTermId)?.termIdParams?.term;
                const prevResidualType = prevOfferInputs?.overridesByTerm?.[prevMonths as number]?.residualInfo?.baseResidualType;
                const newResidualType = inputs.overridesByTerm?.[newMonths as number]?.residualInfo?.baseResidualType;
                if (
                    prevMonths != null &&
                    newMonths != null &&
                    prevMonths !== newMonths &&
                    prevResidualType != null &&
                    newResidualType !== prevResidualType
                ) {
                    inputs = {
                        ...inputs,
                        overridesByTerm: {
                            ...inputs.overridesByTerm,
                            [newMonths]: {
                                ...inputs.overridesByTerm?.[newMonths],
                                residualInfo: {
                                    ...inputs.overridesByTerm?.[newMonths]?.residualInfo,
                                    baseResidualType: prevResidualType as ResidualValueType
                                }
                            }
                        } as IPencilOfferInputs['overridesByTerm']
                    };
                }
            }

            const pencilInputs: IPenciledInputs = {
                ...state,
                byOfferType: updateOfferTypeWithInputs(state.byOfferType, inputs, offerT)
            };
            const copiedOffer = { ...offer, penciledInputs: pencilInputs };
            const totalIncentives = selectors.findAllIncentiveTotalFromOffer(copiedOffer, offerT);

            (pencilInputs.byOfferType![offerT as keyof typeof state.byOfferType] as IPencilOfferInputs).totalIncentives =
                totalIncentives;

            return pencilInputs;
        }

        case types.CLEAR_OVERRIDES: {
            return {
                ...state,
                byOfferType: resetPenciledInputsOverrides(state.byOfferType)
            };
        }

        case types.CLEAR_OVERRIDES_WITHOUT_INCENTIVES: {
            return {
                ...state,
                byOfferType: resetPenciledInputsOverrides(state.byOfferType, false)
            };
        }

        case types.PARTIAL_RESET_OVERRIDES: {
            const termMonth = selectors.getOfferDetailsMonth({ offer }, offerType);
            const meta = action.meta || {};
            const {
                retainRatesOverride = false,
                previousTermMonth,
                retainSellRateOverride = false,
                retainAcqFeeOverride = false,
                retainSecurityDepositOverride = false
            } = meta;
            const inputs = state.byOfferType?.[offerType] || {};

            const currentPQSellRate = inputs.overridesByTerm?.[termMonth!]?.sellRate;
            const previousPQSellRate = inputs.overridesByTerm?.[previousTermMonth]?.sellRate;
            const upqSellRate = inputs.sellRateOverride;

            const retainedSellRate = currentPQSellRate || previousPQSellRate || upqSellRate;
            const retainedResidualType =
                offerType === LEASE && !isUserProgramQuotes && offer?.featureToggles?.enableExplicitLenderP1
                    ? inputs.overridesByTerm?.[termMonth!]?.residualInfo?.baseResidualType
                    : undefined;
            const keptGridPricedTerms =
                isPaymentGridRedesignEnabled && offerType === LEASE ? userPricedGridOverrides(inputs, termMonth) : undefined;

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        rateTypeOverride: undefined,
                        // Keep general sell rate only in case global payment input (e.g. credit rating) changes in FINANCE. It's always APR value.
                        sellRateOverride: retainSellRateOverride ? retainedSellRate : undefined,
                        buyRateOverride: undefined,
                        securityDepositOverride: retainSecurityDepositOverride ? inputs.securityDepositOverride : undefined,
                        ...resolveAcqFeeReset(
                            inputs,
                            selectors.getGovernmentFeesOverride({ offer: { ...offer, offerType } }),
                            retainAcqFeeOverride
                        ),
                        adjustedResidualAmountOverride: undefined,
                        adjustedResidualPercentOverride: undefined,
                        adjustedResidualTypeOverride: undefined,
                        baseResidualAmountOverride: undefined,
                        baseResidualPercentOverride: undefined,
                        programResidualOverride: undefined,
                        programResidualPercentOverride: undefined,
                        baseResidualType: undefined,
                        overridesByTerm: {
                            ...keptGridPricedTerms,
                            ...(retainRatesOverride && inputs.overridesByTerm),
                            [termMonth!]: {
                                ...(retainRatesOverride && { sellRate: retainedSellRate }),
                                ...(retainedResidualType != null && { residualInfo: { baseResidualType: retainedResidualType } })
                            }
                        },
                        [OFFER_ENCRYPTION_FIELDS.OVERRIDES]: ''
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.LOCATION_RESET_OVERRIDES: {
            const meta = action.meta || {};
            const { currentTermMonth } = meta;

            const inputs = state.byOfferType?.[offerType] || {};
            const currentPQSellRate = inputs.overridesByTerm?.[currentTermMonth]?.sellRate;
            const upqSellRate = inputs.sellRateOverride;

            const withoutGovFees = (fee: any) => fee.category !== FeesCategoriesType.GOVERNMENT;

            const sellRateByDealType = {
                overridesByTerm: {
                    ...inputs.overridesByTerm,
                    [currentTermMonth]: {
                        ...inputs.overridesByTerm?.[currentTermMonth],
                        sellRate: currentPQSellRate || upqSellRate
                    }
                },
                ...withoutTypedBuyRate(inputs, currentTermMonth)
            };

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [FINANCE]: {
                        ...state.byOfferType?.[FINANCE],
                        taxItems: undefined,
                        feesOverride: state.byOfferType?.[FINANCE]?.feesOverride?.filter(withoutGovFees),
                        ...(FINANCE === offerType ? sellRateByDealType : {})
                    },
                    [LEASE]: {
                        ...state.byOfferType?.[LEASE],
                        taxItems: undefined,
                        feesOverride: state.byOfferType?.[LEASE]?.feesOverride?.filter(withoutGovFees),
                        ...(LEASE === offerType ? sellRateByDealType : {})
                    },
                    [CASH]: {
                        ...state.byOfferType?.[CASH],
                        taxItems: undefined,
                        feesOverride: state.byOfferType?.[CASH]?.feesOverride?.filter(withoutGovFees)
                    },
                    [BALLOON]: {
                        ...state.byOfferType?.[BALLOON],
                        taxItems: undefined,
                        feesOverride: state.byOfferType?.[BALLOON]?.feesOverride?.filter(withoutGovFees)
                    }
                }
            };
        }

        case types.PARTIAL_RESTORE_OVERRIDES: {
            const { record }: PartialRestoreRecord = action.payload;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        ...record
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }
        case types.SELECTED_CREDIT_PARTIAL_RESET_OVERRIDES: {
            const meta = action.meta || {};
            const {
                retainRatesOverride = false,
                previousTermMonth,
                retainSellRateOverride = false,
                retainAcqFeeOverride = false
            } = meta;

            // Process both FINANCE and LEASE offer types
            const offerTypes: OfferType[] = [FINANCE, LEASE];
            const updatedByOfferType = { ...state.byOfferType };

            offerTypes.forEach((currentOfferType) => {
                const termMonth = selectors.getOfferDetailsMonth({ offer }, currentOfferType);
                const inputs = state.byOfferType?.[currentOfferType];

                const currentPQSellRate = inputs?.overridesByTerm?.[termMonth!]?.sellRate;
                const previousPQSellRate = inputs?.overridesByTerm?.[previousTermMonth]?.sellRate;
                const upqSellRate = inputs?.sellRateOverride;

                const retainedSellRate = currentPQSellRate || previousPQSellRate || upqSellRate;

                // Apply retainRatesOverride only for FINANCE based on the original repo logic
                const shouldRetainRates = currentOfferType === FINANCE && retainRatesOverride;
                // Apply retainSellRateOverride only for FINANCE
                const shouldRetainSellRate = currentOfferType === FINANCE && retainSellRateOverride;

                updatedByOfferType[currentOfferType] = {
                    ...state.byOfferType?.[currentOfferType],
                    rateTypeOverride: undefined,
                    // Keep general sell rate only in case global payment input (e.g. credit rating) changes in FINANCE. It's always APR value.
                    sellRateOverride: shouldRetainSellRate ? retainedSellRate : undefined,
                    buyRateOverride: undefined,
                    securityDepositOverride: undefined,
                    ...resolveAcqFeeReset(
                        inputs,
                        selectors.getGovernmentFeesOverride({ offer: { ...offer, offerType: currentOfferType } }),
                        retainAcqFeeOverride
                    ),
                    adjustedResidualAmountOverride: undefined,
                    adjustedResidualPercentOverride: undefined,
                    adjustedResidualTypeOverride: undefined,
                    baseResidualAmountOverride: undefined,
                    baseResidualPercentOverride: undefined,
                    programResidualOverride: undefined,
                    programResidualPercentOverride: undefined,
                    baseResidualType: undefined,
                    overridesByTerm: {
                        ...(shouldRetainRates && inputs?.overridesByTerm),
                        [termMonth!]: shouldRetainRates ? { sellRate: retainedSellRate } : {}
                    },
                    [OFFER_ENCRYPTION_FIELDS.OVERRIDES]: ''
                };
            });

            return {
                ...state,
                byOfferType: updatedByOfferType as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.HONOR_LEASE_MILEAGE_CHANGE: {
            const termMonth = selectors.getOfferDetailsMonth({ offer }, offerType);
            const inputs = state.byOfferType?.[offerType] || {};

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        securityDepositOverride: undefined,
                        sellRateOverride: undefined,
                        overridesByTerm: {
                            [termMonth!]: {
                                ...inputs.overridesByTerm?.[termMonth!],
                                residualInfo: undefined,
                                sellRate: undefined
                            }
                        },
                        ...withoutTypedBuyRate(inputs, termMonth!),
                        [OFFER_ENCRYPTION_FIELDS.OVERRIDES]: ''
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        case types.FULL_RESET_OVERRIDES: {
            const currentOfferType = action.payload || offerType;
            const { forEachDealType } = action.meta || {};

            const offerTypesToReset = forEachDealType ? [FINANCE, LEASE, CASH] : [currentOfferType];

            const updatedByOfferType = offerTypesToReset.reduce<Record<string, IPencilOfferInputs>>((acc, ot) => {
                acc[ot] = wipeOverridesNode(
                    (state.byOfferType?.[ot as keyof typeof state.byOfferType] || {}) as IPencilOfferInputs,
                    offer.leaseMileage
                );
                return acc;
            }, {});

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    ...updatedByOfferType
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }
        case types.CHANGE_VEHICLE_UPDATE_OVERRIDES: {
            const currentOfferType: OfferType = action.payload || offerType;
            const { forEachDealType, preserveForFallback } = action.meta || {};
            const preserve = preserveForFallback === true;

            const offerTypesToReset: OfferType[] = forEachDealType ? [FINANCE, LEASE, CASH] : [currentOfferType];

            const currentNode = state.byOfferType?.[currentOfferType] || ({} as IPencilOfferInputs);
            const snapshotForCurrent = preserve ? pickFallbackSnapshot(currentNode, offer, currentOfferType) : undefined;

            const updatedByOfferType = offerTypesToReset.reduce<Record<string, IPencilOfferInputs>>((acc, ot) => {
                const node = state.byOfferType?.[ot] || ({} as IPencilOfferInputs);
                const wiped = wipeOverridesNode(node, offer.leaseMileage);
                acc[ot] =
                    ot === currentOfferType && snapshotForCurrent
                        ? { ...wiped, overridesFallbackSnapshot: snapshotForCurrent }
                        : wiped;
                return acc;
            }, {});

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    ...updatedByOfferType
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }
        case types.RESTORE_OVERRIDES_FOR_FALLBACK: {
            const node = state.byOfferType?.[offerType];
            const snapshot = node?.overridesFallbackSnapshot;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...node,
                        ...snapshot,
                        honorOverrides: true,
                        overridesFallbackSnapshot: undefined
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }
        case types.CLEAR_OVERRIDES_FALLBACK_SNAPSHOT: {
            const node = state.byOfferType?.[offerType];
            if (!node?.overridesFallbackSnapshot) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: { ...node, overridesFallbackSnapshot: undefined }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }
        case types.CAPTURE_OVERRIDES_FALLBACK_SNAPSHOT: {
            const node = state.byOfferType?.[offerType];
            if (!node) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...node,
                        overridesFallbackSnapshot: pickFallbackSnapshot(node, offer, offerType)
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }
        case types.APPLY_RECOMMENDED_VEHICLE_PAYMENT_DETAILS: {
            if (offerType !== FINANCE) {
                return state;
            }

            const paymentDetails = action.payload;
            const { downPayment, sellingPrice, retailPrice, apr, buyRate, sellRate } = paymentDetails;

            const termMonth = selectors.getOfferDetailsMonth({ offer }, offerType);
            const prevOverridesByTerm = state.byOfferType?.[offerType]?.overridesByTerm || {};

            const skipRateOverride = Boolean(action.meta?.skipRateOverride);
            const sellRateForOverride = sellRate ?? apr;
            // Applying an AVO card's rate is authoritative, so clear the complementary override field. A new
            // scenario cloned from another carries that scenario's rate overrides; without this, a stale rate
            // shadows the applied card rate. Clear only the APPLIED term's per-term rate (not the whole map) so
            // grid rates the user typed on other terms — and residualInfo on this term — are preserved.
            const clearAppliedTermRate: IPencilOfferInputs['overridesByTerm'] = {
                ...prevOverridesByTerm,
                [termMonth!]: { ...prevOverridesByTerm[termMonth!], sellRate: undefined, buyRate: undefined }
            };
            const rateOverrides: Partial<IPencilOfferInputs> = skipRateOverride
                ? { sellRateOverride: undefined, buyRateOverride: undefined, overridesByTerm: clearAppliedTermRate }
                : isUserProgramQuotes
                ? {
                      sellRateOverride: sellRateForOverride,
                      buyRateOverride: buyRate,
                      overridesByTerm: clearAppliedTermRate
                  }
                : {
                      sellRateOverride: undefined,
                      buyRateOverride: undefined,
                      overridesByTerm: {
                          ...prevOverridesByTerm,
                          [termMonth!]: {
                              ...prevOverridesByTerm[termMonth!],
                              sellRate: sellRateForOverride,
                              buyRate: buyRate
                          }
                      }
                  };

            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        downPayment,
                        offerPrice: sellingPrice,
                        retailPriceOverride: retailPrice,
                        ...(skipRateOverride ? {} : { rateTypeOverride: 'APR' }),
                        ...rateOverrides
                    }
                } as { [offerType in PenciledInputOfferType]: IPencilOfferInputs }
            };
        }

        default: {
            return state;
        }
    }
};
